import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Label } from '@hqo/ui-atoms';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { PhoneInput } from '@hqo/phone-input';
import { Loader2 } from 'lucide-react';
import { cn } from '@hqo/react-hooks';
import type { Value as PhoneNumber, Country } from 'react-phone-number-input';
import { getCountryCallingCode } from 'libphonenumber-js';
import { CreateVisitFormV2Values } from '../../../../types/form-data.types';
import { useVisitorTypeahead } from './hooks/use-visitor-typeahead.hook';
import { useValidationMessage } from '../../../../hooks/use-validation-message.hook';
import { VisitorDropdownItem } from './visitor-dropdown-item';
import { useLocale } from 'shared/hooks/use-locale/use-locale.hook';

interface TypeaheadPhoneFieldProps {
  control: Control<CreateVisitFormV2Values>;
  setValue: UseFormSetValue<CreateVisitFormV2Values>;
  visitorIndex: number;
  required?: boolean;
  error?: { message?: string };
  disabled?: boolean;
}

export const TypeaheadPhoneField = ({
  control,
  setValue,
  visitorIndex,
  required,
  error,
  disabled = false,
}: TypeaheadPhoneFieldProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const formatValidationMessage = useValidationMessage();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const locale = useLocale();

  const defaultCountry = useMemo((): Country => {
    const [, country] = locale?.split(/[-_]/) ?? [];
    return (country?.toUpperCase() as Country) || 'US';
  }, [locale]);

  const fieldName = `visitors.${visitorIndex}.phone` as const;

  const { visitorOptions, isLoading, isDropdownVisible, searchVisitor, selectVisitor, openDropdown, closeDropdown } =
    useVisitorTypeahead({
      setValue,
      visitorIndex,
    });

  const showError = Boolean(error);
  const typedNumberRef = useRef<string>('');
  const skipNextSearchRef = useRef(false);
  const [selectedCountry, setSelectedCountry] = useState<Country>(defaultCountry);

  useEffect(() => {
    setSelectedCountry(defaultCountry);
  }, [defaultCountry]);

  const handleChange = useCallback(
    (fieldOnChange: (value: PhoneNumber | undefined) => void) => (value: PhoneNumber | undefined) => {
      if (skipNextSearchRef.current) {
        skipNextSearchRef.current = false;
        fieldOnChange(value);
        return;
      }

      const countryCallingCode = getCountryCallingCode(selectedCountry);
      const allDigits = (value || '').replace(/\D/g, '');

      let nationalNumber = allDigits;
      if (allDigits.startsWith(countryCallingCode)) {
        nationalNumber = allDigits.slice(countryCallingCode.length);
      }

      typedNumberRef.current = nationalNumber;
      fieldOnChange(value);

      if (nationalNumber.length === 0) {
        return;
      }

      const searchQuery = `+${countryCallingCode}${nationalNumber}`;
      searchVisitor(searchQuery);
      openDropdown();
    },
    [selectedCountry, searchVisitor, openDropdown],
  );

  const handleCountryChange = useCallback(
    (country: Country | undefined) => {
      if (!country) {
        return;
      }

      setSelectedCountry(country);
      skipNextSearchRef.current = true;

      const typedNumber = typedNumberRef.current;
      if (!typedNumber) {
        return;
      }

      const newCountryCode = getCountryCallingCode(country);
      const searchQuery = `+${newCountryCode}${typedNumber}`;
      searchVisitor(searchQuery);
      openDropdown();
    },
    [searchVisitor, openDropdown],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Tab') {
        closeDropdown();
      }
    },
    [closeDropdown],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeDropdown]);

  const emptyResultsMessage = formatMessage({
    id: 'visitCreationForm.visitors.visitorSearchEmpty.phone.title',
    defaultMessage: 'No visitors found with this phone number',
  });

  return (
    <div className="space-y-2">
      <Label htmlFor={fieldName} className="text-sm font-medium text-foreground gap-1">
        {formatMessage({ id: 'createVisitFormV2.visitStep.phone', defaultMessage: 'Phone number' })}
        {required && <span className="text-destructive">*</span>}
      </Label>

      <div className="relative" ref={dropdownRef}>
        <Controller
          name={fieldName as 'visitors.0.phone'}
          control={control}
          // eslint-disable-next-line react/jsx-no-bind
          render={({ field }) => (
            <PhoneInput
              id="phone"
              type="tel"
              name={field.name}
              onBlur={field.onBlur}
              value={field.value as PhoneNumber}
              onChange={handleChange(field.onChange)}
              onCountryChange={handleCountryChange}
              onKeyDown={handleKeyDown}
              placeholder={formatMessage({
                id: 'createVisitFormV2.visitStep.placeholders.phone',
                defaultMessage: 'e.g. +1234567890',
              })}
              className={cn('border border-transparent rounded-[12px]', showError && 'border-system-pink border-[1px]')}
              data-cy="phone"
              defaultCountry={selectedCountry}
              disabled={disabled}
            />
          )}
        />
        {isLoading && (
          <div className="absolute right-12 top-1/2 -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          </div>
        )}

        {!disabled && isDropdownVisible && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {isLoading && (
              <div className="px-3 py-4 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            )}
            {!isLoading && visitorOptions.length > 0 && (
              <div className="py-1">
                {visitorOptions.map((visitor) => (
                  <VisitorDropdownItem
                    key={visitor.uuid}
                    visitor={visitor}
                    onSelect={selectVisitor}
                    subtitleField="phone"
                  />
                ))}
              </div>
            )}
            {!isLoading && visitorOptions.length === 0 && (
              <div className="px-3 py-4 text-center">
                <p className="text-sm text-gray-500">{emptyResultsMessage}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {formatMessage({
                    id: 'visitCreationForm.visitors.visitorSearchEmpty.phone.subtitle',
                    defaultMessage: 'Please try a different search or continue with the form',
                  })}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {showError && <p className="text-sm text-destructive">{formatValidationMessage(error?.message)}</p>}
    </div>
  );
};
